import { useTranslation } from 'react-i18next';
import type { MergeToastState } from '@/hooks/useMergeUndoToast';
import styles from './MergeUndoToast.module.css';

/** Presentational merge-undo toast (rule #30 layer 4). State + timers live in
 *  the `useMergeUndoToast` hook; this is the bottom-center toast row with the
 *  Undo action, shared by PersonView + the Garden grid (manual-merge arc M2).
 *  Own file (no hook export) so react-refresh stays happy. */

interface MergeUndoToastProps {
  toast: MergeToastState;
  onUndo: () => void;
  /** Pause the auto-dismiss while hovered/focused (the hook's cancelDismiss). */
  onPauseDismiss: () => void;
  /** Resume the auto-dismiss on leave/blur (the hook's scheduleDismiss). */
  onResumeDismiss: () => void;
}

export default function MergeUndoToast({
  toast,
  onUndo,
  onPauseDismiss,
  onResumeDismiss,
}: MergeUndoToastProps) {
  const { t } = useTranslation();
  return (
    <div
      className={styles.mergeToast}
      role="status"
      aria-live="assertive"
      onMouseEnter={onPauseDismiss}
      onMouseLeave={onResumeDismiss}
      onFocus={onPauseDismiss}
      onBlur={onResumeDismiss}
    >
      <span className={styles.mergeToastMessage}>
        {t('people.mergedToast', { absorbed: toast.absorbed, kept: toast.kept })}
      </span>
      <button type="button" className={styles.mergeToastAction} onClick={onUndo}>
        {t('people.mergedUndo')}
      </button>
    </div>
  );
}
